import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { adminListMedia, adminDeleteMedia, adminUploadImage } from "../lib/api";
import { resolveImage } from "../lib/settings";
import { useToast } from "../hooks/use-toast";
import { Upload, Trash2, Loader2, Copy, Check, Search, Image as ImageIcon } from "lucide-react";

const formatBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaLibraryDialog = ({ open, onOpenChange, onSelect, mode = "pick" }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListMedia();
      setItems(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) {
          toast({ title: `${f.name} is too large (max 8MB)`, variant: "destructive" });
          continue;
        }
        await adminUploadImage(f);
      }
      toast({ title: `${files.length} file${files.length > 1 ? "s" : ""} uploaded` });
      await load();
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Error", variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (name) => {
    if (!window.confirm("Delete this image? Tours or pages using this URL will show a broken image.")) return;
    try {
      await adminDeleteMedia(name);
      setItems((prev) => prev.filter((i) => i.name !== name));
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  const filtered = items.filter((i) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (i.original_name || "").toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[960px] max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-[22px]">Media library</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 pb-3">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ri-muted)]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filenames"
              className="pl-8"
            />
          </div>
          <label className="btn-primary text-[13px] inline-flex items-center gap-2 cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[var(--ri-red)]" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-[var(--ri-muted)]">
              <ImageIcon size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-[14px]">No images yet. Upload one above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filtered.map((it) => (
                <div key={it.name} className="group rounded-xl border border-[var(--ri-border)] bg-white overflow-hidden hover:shadow-lg transition-shadow">
                  <button
                    type="button"
                    onClick={() => {
                      if (mode === "pick" && onSelect) {
                        onSelect(it.url);
                        onOpenChange(false);
                      }
                    }}
                    className="relative block w-full aspect-square bg-[var(--ri-cream-2)]"
                  >
                    <img src={resolveImage(it.url)} alt={it.original_name} className="absolute inset-0 w-full h-full object-cover" />
                    {mode === "pick" && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <span className="text-white text-[13px] font-medium bg-[var(--ri-red)] px-3 py-1.5 rounded-full">Use this</span>
                      </div>
                    )}
                  </button>
                  <div className="p-2.5">
                    <div className="text-[11.5px] font-medium truncate" title={it.original_name}>{it.original_name || it.name}</div>
                    <div className="text-[10.5px] text-[var(--ri-muted)]">{formatBytes(it.size)}</div>
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyUrl(it.url)}
                        className="flex-1 text-[10.5px] px-2 py-1 rounded bg-[var(--ri-cream)] hover:bg-[var(--ri-cream-2)] inline-flex items-center justify-center gap-1"
                      >
                        {copied === it.url ? <><Check size={11} /> Copied</> : <><Copy size={11} /> URL</>}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(it.name)}
                        className="text-[10.5px] p-1 rounded text-red-600 hover:bg-red-50"
                        aria-label="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-[var(--ri-border)] text-[11.5px] text-[var(--ri-muted)] flex items-center justify-between">
          <span>{items.length} file{items.length !== 1 ? "s" : ""} in library</span>
          <span>Max 8MB per file · JPG, PNG, WEBP, GIF</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibraryDialog;
